import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import logo from '../assets/logo.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      let errorMessage = 'Failed to sign in. Please check your credentials.';
      
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email. Please check your email or create an account.';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again or reset your password.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address. Please check and try again.';
      } else if (err.code === 'auth/configuration-not-found') {
        errorMessage = 'Authentication is not configured. Please enable Email/Password authentication in Firebase.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }
    
    try {
      await resetPassword(email);
      setError('Password reset email sent! Check your inbox.');
    } catch (err) {
      setError('Failed to send password reset email.');
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Premium Background with Textures */}
      <div className="absolute inset-0 bg-gradient-to-br from-olive-900 via-olive-800 to-emerald-900">
        {/* Texture Overlay */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        {/* Gradient Overlay for Depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-transparent"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-olive-600/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-40 h-40 bg-emerald-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-green-600/20 rounded-full blur-2xl animate-pulse delay-500"></div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-2 sm:px-4">
        <div className="max-w-md w-full">
          {/* Premium Glass Card */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-olive-600 via-emerald-600 to-green-600 rounded-3xl blur-lg opacity-30"></div>
            
            {/* Main Card */}
            <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
              {/* Inner Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-3xl"></div>
              
              {/* Content */}
              <div className="relative p-4 sm:p-8">
                {/* Logo Section */}
                <div className="text-center mb-8">
                  <div className="relative inline-block mb-6">
                    {/* Logo Glow */}
                    <div className="absolute inset-0 bg-gradient-to-r from-olive-600 to-emerald-600 rounded-2xl blur-xl opacity-50 scale-110"></div>
                    
                    {/* Logo Container */}
                    <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-olive-600 via-emerald-600 to-green-600 rounded-2xl flex items-center justify-center shadow-2xl border border-white/10">
                      <img src={logo} alt="TravelSplit" className="w-12 h-12 sm:w-16 sm:h-16 object-contain" />
                    </div>
                  </div>
                  
                  <h1 className="text-2xl sm:text-4xl font-bold mb-2">
                    <span className="font-black tracking-wide" style={{ fontFamily: 'Playfair Display, Georgia, serif', letterSpacing: '0.05em' }}>
                      <span className="text-black">Travel</span>
                      <span style={{ color: '#166534' }}>Split</span>
                    </span>
                  </h1>
                  <p className="text-gray-700 mt-2 text-sm sm:text-base">Welcome back! Please sign in to your account.</p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-6 p-4 bg-red-800/30 backdrop-blur border border-red-700/40 rounded-xl flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" />
                    <span className="text-red-200 text-sm">{error}</span>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  {/* Email Input */}
                  <div className="space-y-2">
                    <label className="text-gray-800 text-xs sm:text-sm font-medium tracking-wide">
                      Email Address
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-olive-600/20 to-emerald-600/20 rounded-xl blur-sm group-hover:blur-md transition-all"></div>
                      <Mail className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-olive-200/60 z-10" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="relative w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 bg-white/20 backdrop-blur border border-white/30 rounded-xl text-green-900 placeholder-olive-200/70 focus:outline-none focus:ring-2 focus:ring-olive-500/50 focus:border-olive-400/50 transition-all duration-300 z-10 text-sm sm:text-base"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>

                  {/* Password Input */}
                  <div className="space-y-2">
                    <label className="text-gray-800 text-xs sm:text-sm font-medium tracking-wide">
                      Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-olive-600/20 to-emerald-600/20 rounded-xl blur-sm group-hover:blur-md transition-all"></div>
                      <Lock className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-olive-200/60 z-10" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="relative w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 bg-white/20 backdrop-blur border border-white/30 rounded-xl text-green-900 placeholder-olive-200/70 focus:outline-none focus:ring-2 focus:ring-olive-500/50 focus:border-olive-400/50 transition-all duration-300 z-10 text-sm sm:text-base"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-olive-200/60 hover:text-olive-200/80 transition-colors z-10"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Forgot Password */}
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 font-medium transition-colors duration-200"
                    >
                      Forgot password? 
                      Please Check Spam Folder
                    </button>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="relative w-full py-3 sm:py-4 px-4 sm:px-6 bg-gradient-to-r from-green-800 via-green-700 to-green-800 text-white font-bold rounded-xl hover:from-green-900 hover:via-green-800 hover:to-green-900 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] text-sm sm:text-base"
                  >
                    <div className="flex items-center justify-center">
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Signing in...
                        </>
                      ) : (
                        <>
                          <span className="mr-2"></span>
                          Sign In to Dashboard
                        </>
                      )}
                    </div>
                  </button>
                </form>

                {/* Sign Up Link */}
                <div className="mt-8 text-center">
                  <p className="text-gray-700 text-xs sm:text-sm">
                    New to TravelSplit?{' '}
                    <Link
                      to="/register"
                      className="font-medium text-gray-800 hover:text-gray-900 transition-colors duration-200 underline underline-offset-2 decoration-2 decoration-gray-400/50 hover:decoration-gray-300 text-xs sm:text-sm"
                    >
                      Create your free account
                    </Link>
                  </p>
                </div>

                {/* Premium Badge */}
                <div className="mt-6 flex items-center justify-center">
                  <div className="flex items-center space-x-2 px-3 py-1 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur border border-amber-400/30 rounded-full">
                    <span className="text-amber-300 text-xs"></span>
                    <span className="text-black text-xs font-medium">Created By @HarshitAggarwal</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
